import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_TIMEOUT, STORAGE_KEYS } from '@/constants';
import type { ApiError, ApiErrorResponse } from '@/types';
import { MESSAGES } from '@/constants/messages';
import { useAuthStore } from '@/stores';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Flag to prevent multiple refresh token requests
let refreshPromise: Promise<string> | null = null;

// Refresh access token using httpOnly cookie
// Uses singleton pattern to prevent multiple simultaneous refresh requests
const refreshAccessTokenOnce = async (): Promise<string> => {
  // If already refreshing, return the same promise to avoid duplicate requests
  if (!refreshPromise) {
    refreshPromise = axios
      .post<{ data: { accessToken: string } }>(
        `${API_BASE_URL}/auth/admin/refresh-token`,
        {}, // Empty body - refreshToken is sent automatically via httpOnly cookie
        {
          withCredentials: true, // Required to send httpOnly cookie
          timeout: API_TIMEOUT,
          headers: { 'Content-Type': 'application/json' },
        }
      )
      .then(res => {
        const newToken = res.data?.data?.accessToken;
        if (!newToken) throw new Error('Refresh API did not return accessToken');
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null; // Reset for next refresh cycle
      });
  }

  return refreshPromise;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    const requestUrl = (originalRequest?.url ?? '').toString();
    const isLoginApi = requestUrl.includes('/auth/admin/login');
    const isRefreshApi = requestUrl.includes('/auth/admin/refresh-token');

    // Handle 401 Unauthorized -> try refresh -> retry
    if (error.response?.status === 401 && !isLoginApi && !isRefreshApi && originalRequest) {
      // Avoid infinite retry loops.
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessTokenOnce();

          // set token header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // retry request
          return apiClient(originalRequest);
        } catch (refreshErr) {
          // refresh fail -> logout
          useAuthStore.getState().forceLogoutLocal();
          return Promise.reject(refreshErr);
        }
      }
      // If we've already retried, logout
      useAuthStore.getState().forceLogoutLocal();
      return Promise.reject(error);
    }

    // Handle other errors - Map backend response to frontend format
    const apiError: ApiError = {
      code: error.response?.data?.code || 'UNKNOWN_ERROR',
      message: error.response?.data?.message || MESSAGES.ERR_00013,
      errors: error.response?.data?.data,
    };

    return Promise.reject(apiError);
  }
);

export default apiClient;

// Helper functions for API calls
export const get = <T>(url: string, params?: Record<string, unknown>) => {
  return apiClient.get<T>(url, { params });
};

export const post = <T>(url: string, data?: unknown, config?: { signal?: AbortSignal }) => {
  return apiClient.post<T>(url, data, config);
};

export const put = <T>(url: string, data?: unknown) => {
  return apiClient.put<T>(url, data);
};

export const patch = <T>(url: string, data?: unknown) => {
  return apiClient.patch<T>(url, data);
};

export const del = <T>(url: string) => {
  return apiClient.delete<T>(url);
};

export const delWithBody = <T>(url: string, data?: unknown) => {
  return apiClient.delete<T>(url, { data });
};

